import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import type { TimeseriesPoint } from '../types';
import { formatBRL, formatDate, formatNumber } from '../lib/format';

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-default)',
      borderRadius: '8px',
      padding: '10px 14px',
      fontSize: '12px',
      fontFamily: 'var(--font-mono)',
    }}>
      <div style={{ color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'var(--font-sans)' }}>
        {label ? formatDate(label) : ''}
      </div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: '2px' }}>
          {p.name}: {p.name === 'Leads' ? formatNumber(p.value) : formatBRL(p.value)}
        </div>
      ))}
    </div>
  );
}

interface Props {
  data: TimeseriesPoint[];
  showMeta?: boolean;
  showGoogle?: boolean;
  showLeads?: boolean;
  height?: number;
}

export function SpendChart({ data, showMeta = true, showGoogle = false, showLeads = false, height = 200 }: Props) {
  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '12px',
      padding: '20px',
    }}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={v => {
              const [, m, d] = v.split('-');
              return `${d}/${m}`;
            }}
            tick={{ fontSize: 11, fontFamily: 'var(--font-mono)', fill: 'var(--text-tertiary)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="spend"
            tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`}
            tick={{ fontSize: 11, fontFamily: 'var(--font-mono)', fill: 'var(--text-tertiary)' }}
            axisLine={false}
            tickLine={false}
            width={50}
          />
          {showLeads && (
            <YAxis
              yAxisId="leads"
              orientation="right"
              tick={{ fontSize: 11, fontFamily: 'var(--font-mono)', fill: 'var(--text-tertiary)' }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
          )}
          <Tooltip content={<CustomTooltip />} />
          {(showMeta || showGoogle) && <Legend
            wrapperStyle={{ fontSize: '11px', fontFamily: 'var(--font-mono)', paddingTop: '8px' }}
          />}
          {showMeta && (
            <Line
              yAxisId="spend"
              type="monotone"
              dataKey="metaSpend"
              name="Meta"
              stroke="var(--accent)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          )}
          {showGoogle && (
            <Line
              yAxisId="spend"
              type="monotone"
              dataKey="googleSpend"
              name="Google"
              stroke="var(--blue)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          )}
          {showLeads && (
            <Line
              yAxisId="leads"
              type="monotone"
              dataKey="leads"
              name="Leads"
              stroke="var(--green)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              strokeDasharray="4 2"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
